import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
// !FIXME: Remove circular dependency
import {
  AuthCurrentUserActions,
  AuthFacade,
  AuthSelectors,
  getCurrentUser,
} from '@fantaz/shared/auth';
import { PromoCodeMessageDialogComponent } from '@fantaz/promo-code/ui/message-dialog';
import { PromoCodeToastConfirmationComponent } from '@fantaz/promo-code/ui/toast-confirmation';
// !FIXME: Remove circular dependency
import {
  FzGoogleTagManagerService,
  PromoService,
} from '@fantaz/shared/data-access/api';
import { GAEventKeys, PromoCode } from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  RouterActions,
  getAllQueryParameters,
  CoreRouterActions,
  getAllRouteParameters,
} from '@fantaz/core/router';
import { PromoteCodeModalComponent } from '@fantaz/shared/ui/promote-code-modal';
import {
  BaseRouterStoreState,
  SerializedRouterStateSnapshot,
} from '@ngrx/router-store';
import * as ApplyPromoCodeActions from './promo-code.actions';
import {
  AddPromoCodeToAuthUserAfterSignInAction,
  PromoCodeDialogAction,
} from './promo-code.actions';
import { ApplyPromoCodeFacade } from './promo-code.facade';
import * as ApplyPromoCodeSelectors from './promo-code.selectors';

type NavigationEnded = {
  payload: BaseRouterStoreState & {
    routerState: SerializedRouterStateSnapshot;
  };
};

const PROMO_CODE_KEY = 'promocode';

@Injectable()
export class ApplyPromoCodeEffects {
  /**
   * Effect to open promo code dialog
   */
  showPromoCodeDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RouterActions.ROUTER_NAVIGATED),
      mergeMap(({ payload }: NavigationEnded) => {
        const queryParams = getAllQueryParameters(payload.routerState);

        // read if exist promo code in query params

        if (queryParams?.has('dialog')) {
          const showPromoCodeModal = queryParams.get('dialog');
          const value = queryParams.get('value');

          if (showPromoCodeModal === 'promocode') {
            return [
              PromoCodeDialogAction.openDialog(value || '', {
                source: 'refresh',
              }),
            ];
          }
        }

        return [];
      }),
    ),
  );

  openPromoCodeDialogFromQueryRouteParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RouterActions.ROUTER_NAVIGATED),
      mergeMap(({ payload }: NavigationEnded) => {
        const queryParams = getAllQueryParameters(payload.routerState);
        let promo = '';

        if (queryParams?.has(PROMO_CODE_KEY)) {
          promo = queryParams.get(PROMO_CODE_KEY);
          return [
            PromoCodeDialogAction.openDialog(promo.toUpperCase(), {
              source: 'in-query',
            }),
          ];
        }

        return [];
      }),
    ),
  );

  openPromoCodeDialogFromRouteParam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RouterActions.ROUTER_NAVIGATED),
      mergeMap(({ payload }: NavigationEnded) => {
        const queryParams = getAllRouteParameters(payload.routerState);

        if (queryParams?.has(PROMO_CODE_KEY)) {
          const promo = queryParams.get(PROMO_CODE_KEY);

          return [
            PromoCodeDialogAction.openDialog(promo.toUpperCase(), {
              source: 'in-route',
            }),
            // navigate to same route without promo code
            CoreRouterActions.goToRoute('/'),
          ];
        }

        return [];
      }),
    ),
  );

  /**
   * Effect to open promo code dialog, if it is not already opened
   */
  openPromoCodeDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromoCodeDialogAction.openDialog),
      withLatestFrom(
        this.store.select(ApplyPromoCodeSelectors.isShowPromoCodeDialog),
        ({ promoCode, options }, isShowPromoCodeDialog) => ({
          promoCode,
          options,
          isShowPromoCodeDialog,
        }),
      ),
      filter(({ isShowPromoCodeDialog }) => !isShowPromoCodeDialog),
      map(({ promoCode, options }) => {
        return PromoCodeDialogAction.openedDialog(promoCode, options);
      }),
    ),
  );

  openedPromoCodeDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromoCodeDialogAction.openedDialog),
      exhaustMap(({ promoCode, options }) => {
        const ref = this.dialog.open(PromoteCodeModalComponent, {
          disableClose: true,
          data: {
            initialPromoCode: promoCode,
            submitting: this.store.select(ApplyPromoCodeSelectors.isApplying),
            closeOn: this.store.select(
              ApplyPromoCodeSelectors.isApplyingSuccess,
            ),
            submit: (promoCode: string) => {
              if (promoCode) {
                this.store.dispatch(
                  ApplyPromoCodeActions.applyPromoCode({
                    promoCode,
                  }),
                );
              }
            },
            ...options,
          },
        });
        return ref.afterClosed();
      }),
      map(() => PromoCodeDialogAction.closeDialog()),
    ),
  );

  restorePromoCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplyPromoCodeActions.loadPromoCode),
      mergeMap(() => {
        const data = this.promoCodeService.loadFromLocalStorage();
        return [
          ApplyPromoCodeActions.loadPromoCodeSuccess({
            promoCode: data?.value && data.used === false ? data.value : null,
          }),
        ];
      }),
      catchError((error) => {
        return of(ApplyPromoCodeActions.loadPromoCodeFailure({ error }));
      }),
    ),
  );

  /**
   * Effect to add promo to user when user is logged in if promo code exist in
   * local storage
   */
  addPromoCodeToAuthUserAfterSignInEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthCurrentUserActions.loadCurrentUserSuccess),
      mergeMap(() => {
        const data = this.promoCodeService.loadFromLocalStorage();

        if (!data || data.used) {
          return [];
        }

        // remove promo code from local storage
        this.promoCodeService.removePromoCodeFromLocalStorage();

        return [
          AddPromoCodeToAuthUserAfterSignInAction.loadPromoCodeSuccess({
            promoCode: data.value,
          }),
        ];
      }),
      catchError((error) =>
        of(
          AddPromoCodeToAuthUserAfterSignInAction.loadPromoCodeFailure({
            error,
          }),
        ),
      ),
    ),
  );

  handleLoadedSuccessPromoCodeFromLocalStorageEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddPromoCodeToAuthUserAfterSignInAction.loadPromoCodeSuccess),
      mergeMap(({ promoCode }) =>
        this.promoCodeService.applyPromoCode(promoCode as string).pipe(
          map((data) =>
            AddPromoCodeToAuthUserAfterSignInAction.addPromoCodeSuccess({
              promoCode: data,
            }),
          ),
          catchError((error) =>
            of(
              AddPromoCodeToAuthUserAfterSignInAction.addPromoCodeFailure({
                error,
                promoCode,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  handleAddedSuccessPromoCodeFromLocalStorageEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddPromoCodeToAuthUserAfterSignInAction.addPromoCodeSuccess),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      mergeMap(([{ promoCode }, currentUser]) => {
        if (promoCode.urlLandingPage) {
          this.toast.openSnackBar(
            `Great, enjoy the benefits of your promo code “${promoCode.uuid}“. We are redirecting you to ${promoCode.description} page.`,
          );
          if (currentUser && currentUser.registrationComplete === false) {
            //
            const path = this.router.url;
            const redirectTo = promoCode.urlLandingPage;

            this.router.navigate([path], {
              queryParams: { redirectTo },
            });
          } else {
            window.location.href = promoCode.urlLandingPage;
          }
        } else {
          this.toast.openSnackBar(
            `Well done! The promo code "${promoCode.uuid}" is awesome.`,
          );
        }
        return [
          ApplyPromoCodeActions.applyPromoCodeSuccess({
            payload: {
              promoCode,
              local: false,
            },
          }),
        ];
      }),
    ),
  );

  handleAddedFailurePromoCodeFromLocalStorageEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddPromoCodeToAuthUserAfterSignInAction.addPromoCodeFailure),
      exhaustMap(({ error, promoCode }) => {
        console.error('Error', error);
        this.promoCodeService.removePromoCodeFromLocalStorage();

        const ref = this._snackBar.openFromComponent(
          PromoCodeToastConfirmationComponent,
          {
            data: {
              primaryButtonText: 'Use another',
              enabledActions: true,
              message: `Sorry, the promo code “${promoCode}” has either expired, or it is not applicable for this tournament. Please try again with a different promo code.`,
            },
            duration: 1800000,
            verticalPosition: 'top',
          },
        );

        return ref.afterDismissed();
      }),
      mergeMap((result) => {
        // On cancel or dismiss the toast, we will use the default data to
        // continue with the confirmation
        if (result?.dismissedByAction) {
          return [
            PromoCodeDialogAction.openDialog('', {
              source: 'use-another',
            }),
          ];
        }

        return [];
      }),
    ),
  );

  /**
   * Effect to save promo code into local storage
   */
  savePromoCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplyPromoCodeActions.savePromoCode),
      mergeMap(({ promoCode }) => {
        return this.promoCodeService.saveInLocalStorage(promoCode).pipe(
          map(() => {
            return ApplyPromoCodeActions.savePromoCodeSuccess({ promoCode });
          }),
          catchError((error) => {
            console.error('Error', error);
            // TODO: notify user of failure
            return of(
              ApplyPromoCodeActions.savePromoCodeFailure({ error, promoCode }),
            );
          }),
        );
      }),
    ),
  );

  /**
   * Effect to remove promo code
   */
  removePromoCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplyPromoCodeActions.removePromoCode),
      mergeMap(({ options }) => {
        // load promo code from local storage
        return this.promoCodeService
          .removeFromLocalStorage({
            ...options,
          })
          .pipe(
            map(() => {
              return ApplyPromoCodeActions.removePromoCodeSuccess();
            }),
            catchError((error) => {
              console.error('Error', error);
              // notify user of failure
              return of(
                ApplyPromoCodeActions.removePromoCodeFailure({ error }),
              );
            }),
          );
      }),
    ),
  );

  /**
   * Effect to apply promo code, if user is not authenticated then store it in
   * local storage, otherwise validate promo code and apply it.
   */
  applyPromoCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplyPromoCodeActions.applyPromoCode),
      withLatestFrom(this.authFacade.authUser$),
      mergeMap(([{ promoCode }, user]) => {
        // if user is authenticated, then validate promo code and apply it
        if (user?.uuid) {
          return this.promoCodeService.applyPromoCode(promoCode).pipe(
            map((data) => {
              // remove promo code from local storage in case it was stored
              this.promoCodeService.removePromoCodeFromLocalStorage();

              if (data.urlLandingPage) {
                this.toast.openSnackBar(
                  `Great, enjoy the benefits of your promo code “${promoCode}“. We are redirecting you to ${data.description} page.`,
                );
                window.location.href = data.urlLandingPage;
              } else {
                this.toast.openSnackBar(
                  `Great, enjoy the benefits of your promo code “${promoCode}“.`,
                );
              }
              return ApplyPromoCodeActions.applyPromoCodeSuccess({
                payload: {
                  promoCode: data,
                  local: false,
                },
              });
            }),
            catchError((error) => {
              console.error('Error', error);
              this.toast.openSnackBar(error.message);
              return of(
                ApplyPromoCodeActions.applyPromoCodeFailure({
                  error,
                  promoCode,
                }),
              );
            }),
          );
        }

        // store promo code in local storage
        return this.promoCodeService.saveInLocalStorage(promoCode).pipe(
          exhaustMap(() => {
            this.dialog.open(PromoCodeMessageDialogComponent, {
              data: {
                promoCode,
              },
            });

            return [
              ApplyPromoCodeActions.applyPromoCodeSuccess({
                payload: {
                  promoCode,
                  local: true,
                },
              }),
            ];
          }),
          catchError((error) => {
            console.error('Error', error);
            // TODO: notify user of failure
            return of(
              ApplyPromoCodeActions.applyPromoCodeFailure({ error, promoCode }),
            );
          }),
        );
      }),
    ),
  );

  reportToGoogleAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApplyPromoCodeActions.applyPromoCodeSuccess),
        withLatestFrom(this.store.select(getCurrentUser)),
        tap(([{ payload }, currentUser]) => {
          this.googleTagManager.pushTag({
            event: GAEventKeys.PROMO_CODE,
            promo_code_action: payload.local
              ? 'stored_promo_code'
              : 'confirmed_promo_code',
            promo_code: payload.local
              ? (payload.promoCode as string)
              : (payload.promoCode as PromoCode).uuid,
            ...(!currentUser && {
              unauthenticated: true,
            }),
            ...(currentUser && {
              user_id: currentUser?.user.uuid,
              user_name: currentUser?.user.username,
              sponsor: currentUser?.user.sponsorUsername || '',
            }),
            ...(payload.silent && { silent: true }),
          });
        }),
      ),
    { dispatch: false },
  );

  handlePromoCodeFailureEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApplyPromoCodeActions.applyPromoCodeFailure),
        tap(({ promoCode }) => {
          this.toast.openSnackBar(
            `Sorry, the promo code "${promoCode}" has either expired, or it is not applicable for this tournament. Please try again with a different promo code.`,
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly authFacade: AuthFacade,
    private readonly promoCodeService: PromoService,
    private readonly toast: ToastService,
    private _snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private googleTagManager: FzGoogleTagManagerService,
    private facade: ApplyPromoCodeFacade,
    private router: Router,
  ) {}
}
